import { find } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { useSessionStorage } from 'usehooks-ts';
import { useShallow } from 'zustand/react/shallow';

import {
  Constants,
  ProviderType,
  SHOULD_GET_HG_DATA,
} from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { useChipStore } from '../../store/useChipStore';
import { ChipState } from '../../store/useChipStore/chipStore';
import { useDetailsStore } from '../../store/useDetailsStore';
import { DetailsStore } from '../../store/useDetailsStore/detailsStore';
import { shouldSuppressVirtualOnlyLocationIndicator } from '../../utils/indicator.utils';
import {
  getDetailsNormalizedProviderId,
  getNetworkStatus,
  getSelectedLocationIndex,
} from '../../utils/providerDetails.utils';
import {
  getCoverageTypes,
  getCurrentMember,
  memberHasBHCoverage,
} from '../../utils/user.utils';
import { useFeatureFlag } from '../useFeatureFlag';
import { useLagoon } from '../useLagoon';
import { useProviderDetails } from '../useProviderDetails';

export const useProviderDetailsData = () => {
  const [searchParams] = useSearchParams();
  const searchParamsObject = Object.fromEntries(searchParams.entries());
  const {
    providerId: providerIdFromParams,
    locationId: locationIdFromParams,
    isTieredDataCard: isTieredDataCardFromParams,
    isTieredSnackCard: isTieredSnackCardFromParams,
    isOHBS: isOHBSFromParams,
  } = searchParamsObject;

  const [enableProviderDetailsDataFromParams] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS
      .ENABLE_PROVIDER_DETAILS_AND_REVIEWS_FROM_PARAMS,
  ]);
  const currentMember = getCurrentMember();
  const memberPolicyNumber = currentMember?.policyNumber;
  const memberCoverages = getCoverageTypes(currentMember);
  const chipStore = useChipStore(useShallow((state: ChipState) => state));
  const { coverageType } = chipStore;
  const hasBH = memberHasBHCoverage(memberCoverages);
  const lob = currentMember.lineOfBusiness;

  const detailsNormalizedProviderId = getDetailsNormalizedProviderId(
    coverageType,
    lob,
    providerIdFromParams
  );
  const shouldGetHGData = SHOULD_GET_HG_DATA;
  const featureSuppression = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const suppressedVirtualVisitPolicyIds = find(featureSuppression, {
    DNT_feature:
      ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS.VIRTUAL_CARE_UNET_QUICK_CARE,
    active: true,
  })?.policyIds;
  const featureSuppressionData = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_SUPPRESSION
  )();
  const virtualOnlyLocationIndFlagKey =
    ConstantsLagoon.FEATURE_SUPPRESSION_FLAGS
      .VIRTUAL_ONLY_LOCATION_INDICATOR_UNET_RAILROAD;
  const virtualOnlyLocationInd = shouldSuppressVirtualOnlyLocationIndicator(
    memberPolicyNumber,
    hasBH,
    virtualOnlyLocationIndFlagKey,
    featureSuppressionData
  );

  const [selectedId] = useSessionStorage<string>(
    Constants.STORAGE_KEYS.SESSION.MAP_PIN_SELECTED_ID,
    ''
  );

  const {
    avatar = 'heart',
    showDirection,
    isTieredSnackCardFromStore,
    isTieredDataCardFromStore,
    isOHBSFromStore,
    locationIdFromStore,
    setDetailsStore,
    inNetwork,
    providerName,
    primarySpeciality,
    professionalDesignation,
    rating,
    surveyCount,
    npi,
    imageLocation,
    providerDetails,
    storeProviderId,
  } = useDetailsStore(
    useShallow((state: DetailsStore) => ({
      avatar: state.detailsState.avatar,
      showDirection: state.detailsState.showDirection,
      isTieredSnackCardFromStore: state.detailsState.isTieredSnackCard,
      isTieredDataCardFromStore: state.detailsState.isTieredDataCard,
      isOHBSFromStore: state.detailsState.isOHBS,
      locationIdFromStore: state.detailsState.locationId,
      setDetailsStore: state.setDetailsStore,
      inNetwork: state.detailsState.inNetwork,
      providerName: state.detailsState.providerName,
      primarySpeciality: state.detailsState.primarySpeciality,
      professionalDesignation: state.detailsState.professionalDesignation,
      rating: state.detailsState.rating,
      surveyCount: state.detailsState.surveyCount,
      npi: state.detailsState.npi,
      imageLocation: state.detailsState.imageLocation,
      providerDetails: state.detailsState.providerDetails,
      storeProviderId: state.detailsState.providerId,
    }))
  );

  const isTieredDataCard =
    isTieredDataCardFromParams === 'true' || isTieredDataCardFromStore;

  const isTieredSnackCard =
    isTieredSnackCardFromParams === 'true' || isTieredSnackCardFromStore;

  const isOHBS = isOHBSFromParams === 'true' || isOHBSFromStore;

  const locationId = locationIdFromParams || locationIdFromStore;

  const { data: providerDetailsData, isLoading } = useProviderDetails({
    providerType: ProviderType.PRACTITIONER,
    providerId: detailsNormalizedProviderId,
    isOHBS,
    locationId,
    shouldGetHGData,
    suppressedVirtualVisitPolicyIds,
    virtualOnlyLocationInd,
  });
  const resultFromStore = {
    avatar,
    showDirection,
    isTieredSnackCard,
    isTieredDataCard,
    providerId: detailsNormalizedProviderId,
    isLoading,
    data: providerDetailsData,
    setDetailsStore,
    inNetwork,
    providerName,
    primarySpeciality,
    professionalDesignation,
    rating,
    surveyCount,
    npi,
    providerDetails,
    storeProviderId,
    locationId,
    imageLocation,
    isOHBS,
  };

  // Result from the API
  const resultFromApi = (() => {
    const data = providerDetailsData?.providerDetails?.providerDetails;
    const providerLocations = data?.providerLocations || [];
    const selectedLocationIndex = getSelectedLocationIndex(
      providerLocations,
      selectedId
    );
    const selectedLocation = data?.providerLocations?.[selectedLocationIndex];
    const networkStatus = getNetworkStatus(selectedLocation?.network?.status);

    return {
      avatar: data?.imageLocation,
      showDirection: false,
      isTieredSnackCard,
      isTieredDataCard,
      providerId: detailsNormalizedProviderId,
      isLoading,
      data: providerDetailsData,
      setDetailsStore,
      inNetwork: networkStatus,
      providerName: data?.providerName,
      primarySpeciality: data?.specialties?.join('|'),
      professionalDesignation: data?.primaryDegrees?.[0],
      rating: data?.healthGradeRating,
      surveyCount: data?.surveyCount,
      npi: data?.npi,
      providerDetails: JSON.stringify(data),
      storeProviderId,
      locationId,
      imageLocation: data?.imageLocation,
      isOHBS,
    };
  })();

  if (enableProviderDetailsDataFromParams) {
    return resultFromApi;
  } else {
    return resultFromStore;
  }
};
